import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { Category, CreateCategoryInput } from '@/types/category';
import { CATEGORY_ICONS, CATEGORY_COLORS } from '@/types/category';
import { useTranslation } from '@/i18n';

interface CategoryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCategoryInput) => void;
  category?: Category | null;
  isLoading?: boolean;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  category,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateCategoryInput>({
    name: '',
    icon: 'Tag',
    color: '#3B82F6',
    type: 'expense',
    deductibleBR: false,
    deductibleUK: false,
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        icon: category.icon,
        color: category.color,
        type: category.type || 'expense',
        deductibleBR: category.deductibleBR ?? category.taxDeductibleBr ?? false,
        deductibleUK: category.deductibleUK ?? category.taxDeductibleUk ?? false,
      });
    } else {
      setFormData({
        name: '',
        icon: 'Tag',
        color: '#3B82F6',
        type: 'expense',
        deductibleBR: false,
        deductibleUK: false,
      });
    }
  }, [category, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const renderIcon = (iconName: string, className: string = 'w-5 h-5') => {
    const IconComponent = (Icons as unknown as Record<string, React.ElementType>)[iconName];
    return IconComponent ? <IconComponent className={className} /> : null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? t.app.categories.form.editCategory : t.app.categories.form.newCategory}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <Input
          label={t.app.categories.form.categoryName}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={t.app.categories.form.categoryNamePlaceholder}
          required
        />

        {/* Type */}
        <Select
          label={t.app.categories.form.type}
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
          options={[
            { value: 'expense', label: t.app.categories.type.expense },
            { value: 'income', label: t.app.categories.type.income },
          ]}
        />

        {/* Icon Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t.app.categories.form.icon}
          </label>
          <div className="grid grid-cols-9 gap-1 p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg max-h-32 overflow-y-auto">
            {CATEGORY_ICONS.map((iconName) => (
              <button
                key={iconName}
                type="button"
                onClick={() => setFormData({ ...formData, icon: iconName })}
                className={`p-2 rounded-lg transition-colors ${
                  formData.icon === iconName
                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600'
                    : 'hover:bg-gray-200 dark:hover:bg-secondary-700 text-gray-600 dark:text-gray-400'
                }`}
                title={iconName}
              >
                {renderIcon(iconName, 'w-4 h-4')}
              </button>
            ))}
          </div>
        </div>

        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t.app.categories.form.colour}
          </label>
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-secondary-900 rounded-lg">
            {CATEGORY_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData({ ...formData, color })}
                className={`w-8 h-8 rounded-full transition-transform ${
                  formData.color === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 dark:bg-secondary-900 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t.app.categories.form.preview}:</p>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${formData.color}20`, color: formData.color }}
            >
              {renderIcon(formData.icon)}
            </div>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.name || t.app.categories.form.categoryName}
            </span>
          </div>
        </div>

        {/* Deductible Options */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t.app.categories.form.taxDeductible}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.deductibleBR}
                onChange={(e) => setFormData({ ...formData, deductibleBR: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Brasil</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.deductibleUK}
                onChange={(e) => setFormData({ ...formData, deductibleUK: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">UK</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-secondary-700">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t.app.common.cancel}
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {category ? t.app.categories.form.save : t.app.categories.form.create}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryForm;
